// src/tabs/logements/LivretTab.jsx — PR 3 refonte Logements (ADR-058)
// + ADR-197 (LOT 1) : coupure des fallbacks de CONTENU parent→chambre.
//
// Nouvel onglet "📖 Livret" — sections voyageur (adresse, accès, WiFi,
// parking & poubelles, règles, livret externe) + aperçu live message
// type avec vraies résas filtrées sur le logement courant.
//
// Single source pour `regles_maison` (anciennement AnnoncePubliqueTab → SyncBeds24Tab) et
// `livret_url` (déplacé d'InfoFbzTab). Les autres champs restent
// éditables temporairement aussi côté InfoFbzTab (double saisie acceptée
// pour PR 3, à nettoyer en PR cleanup ultérieure).
//
// ADR-197 (LOT 1) — héritage chambres RETIRÉ :
//   Le SEED (LOT 0) a donné aux chambres lc1/lc2/lc3 leurs valeurs propres
//   (adresse, accès, parking, poubelles, courses, règles, codes, wifi,
//   horaires). Les champs Livret sont désormais des champs standard édités
//   directement sur la chambre — plus de badges « 📍 Hérité » / « 📍 Édité au
//   niveau Maison Zen », plus de mode lecture seule, plus de fallback parent.
//
// Variables exposées = uniquement celles consommées par templates ACTIFS
// (welcome_checkin_v2 aujourd'hui). Anti-pattern ADR-057 : ne pas
// exposer wifi_name/wifi_code séparés, codes individuels (door_code,
// box_code, etc.), parking_info / poubelles_info versions courtes —
// vars dormantes, à réintégrer si templates futurs les consomment.
//
// Pattern aperçu live = MessageEditor.jsx:189 — renderTemplate(body, ctx)
// avec ctx={ reservation, logement, cfg, language }. whiteSpace:pre-wrap
// strict (PAS dangerouslySetInnerHTML).

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import {
  ClipboardList, FileText, BookOpen,
  Home, MapPin, KeyRound, Wifi, DoorOpen, Car, ScrollText,
  Eye, Check, ChevronRight, ArrowRight, PenLine, Link2, Star,
} from "lucide-react"
import { Row, Field, FieldFull, Inp, Select, Textarea, generateTimes, rangeOptions } from "./atoms"
import TaxeBaremeDetector from "./TaxeBaremeDetector"
import { DS } from "../../lib/ui"
import { parseSurfaceM2Input } from "../../lib/surface-m2"
import { resolveCheckinCheckout } from "../../lib/horaires"
import { propDisplayName } from "../../lib/format"
import { supabase } from "../../supabase"
import { renderTemplate } from "../../lib/template-variables"
import FormulairesTab from "./FormulairesTab"
import VariableInsertPanel from "../../components/admin/VariableInsertPanel"
import LivretModulesEditor from "./LivretModulesEditor"
import CopyToChambresButton from "./CopyToChambresButton"
import WifiNetworksEditor from "./WifiNetworksEditor"
import ReglesSejourEditor, { hasRegleSejour } from "./ReglesSejourEditor"
import VilleLogementEditor from "./VilleLogementEditor"

// ADR-197 (LOT 1) : la whitelist locale STRICT_INHERITANCE (champs hérités
// non-overridables côté chambre) a été RETIRÉE avec les modes d'héritage du
// LivretField — chaque chambre lit/écrit ses propres champs seedés (LOT 0).

// WAGON R5 (B2/B4) — options capacité / horaires, reprises byte-identiques
// d'InfoFbzTab (mêmes listes générées) suite au déménagement des champs
// « Nombre personnes MAX » et « ⏰ Horaires check-in/check-out » vers la zone
// Instructions. Paths cfg INCHANGÉS : capacity.max_persons /
// schedule.checkin_start / schedule.checkout_time (consommateurs identiques).
const TIMES = generateTimes()
const PERSONS = rangeOptions(1, 30)

// ── Onglet Instructions en CARTES — maquette #1 (habillage DA) ───────
//
// 7 blocs repliables au clic, état persistant SESSION (sessionStorage), GLOBAL
// (pas par logement). PUR LAYOUT (gate DA) : les paths cfg, les handlers
// (toggle/edit/save) et les data-testid des blocs restent INTACTS — seul
// l'emballage visuel change (icône lucide, regroupement À-compléter/renseignés,
// cercle de progression, panneau aperçu ardoise).
//
// Chaque carte porte une icône lucide + une couleur de SURLIGNAGE (`hl`) reprise
// par les variables de l'aperçu qui proviennent de cette carte (buildVarColorCss).
const SESSION_KEY = "fbz-livret-cards-open"

const CARDS = {
  caracteristiques: { Icon: Home,       title: "Caractéristiques",            hl: "#5a6b4f" },
  adresse:          { Icon: MapPin,     title: "Adresse",                     hl: "#3d5166" },
  codes:            { Icon: KeyRound,   title: "Codes d'accès",               hl: "#9c5e42" },
  wifi:             { Icon: Wifi,       title: "WiFi + Livret externe",       hl: "#0369a1" },
  acces:            { Icon: DoorOpen,   title: "Accès",                       hl: "#9c5e42" },
  parking:          { Icon: Car,        title: "Parking · Poubelles · Courses", hl: "#4a5763" },
  regles:           { Icon: ScrollText, title: "Règles maison",               hl: "#2e3d4d" },
}

const CARD_IDS = Object.keys(CARDS)

// Complétude d'un bloc (dérivation PRÉSENTATION pure — 0 écriture cfg). Sert au
// cercle de progression et au tri « À compléter » / « Renseignés ». On lit le
// champ porteur de sens de chaque bloc dans l'état cfg déjà chargé (et
// wifi_networks chargé à part pour le bloc WiFi).
function isCardFilled(cardId, log, wifiNetworks) {
  const s = (v) => v != null && String(v).trim() !== ""
  switch (cardId) {
    case "caracteristiques": return s(log.floor)
    case "adresse":          return s(log.adresse_complete)
    case "codes":            return s(log.box_code)
    case "wifi":             return (Array.isArray(wifiNetworks) && wifiNetworks.some((n) => s(n?.ssid))) || s(log.livret_url)
    case "acces":            return s(log.instructions_acces_batiment) || s(log.instructions_acces_appartement)
    case "parking":          return s(log.parking_info_long) || s(log.infos_poubelles_long) || s(log.infos_courses_stockage)
    // Règles = texte libre OU au moins une des 2 règles structurées
    // (animaux / fumeur) arbitrée. `hasRegleSejour` n'accepte QUE les booléens
    // stricts : une clé absente ne compte pas comme renseignée.
    case "regles":           return s(log.regles_maison) || hasRegleSejour(log)
    default:                 return false
  }
}

// Mapping variable Mustache → carte source + champ (ref). Les variables non
// listées (orphelines) sont surlignées en GRIS NEUTRE et NON cliquables :
// elles sont éditées ailleurs (InfoFbzTab) ou n'ont pas de champ propre ici.
const VAR_TO_CARD = {
  logement_address_full:           { cardId: "adresse",          fieldName: "adresse_complete" },
  logement_address_precision:      { cardId: "adresse",          fieldName: "adresse_precision" },
  // {ville} → carte Adresse (champ Ville, écrit public_address.ville).
  ville:                           { cardId: "adresse",          fieldName: "public_address.ville" },
  logement_floor:                  { cardId: "caracteristiques", fieldName: "floor" },
  checkin_time:                    { cardId: "caracteristiques", fieldName: "schedule.checkin_start" },
  checkout_time:                   { cardId: "caracteristiques", fieldName: "schedule.checkout_time" },
  access_building_instructions:    { cardId: "acces",            fieldName: "instructions_acces_batiment" },
  access_apartment_instructions:   { cardId: "acces",            fieldName: "instructions_acces_appartement" },
  wifi_instructions:               { cardId: "wifi",             fieldName: "wifi_networks" },
  wifi_name:                       { cardId: "wifi",             fieldName: "wifi_networks" },
  wifi_code:                       { cardId: "wifi",             fieldName: "wifi_networks" },
  box_code:                        { cardId: "codes",            fieldName: "box_code" },
  door_code:                       { cardId: "codes",            fieldName: "door_code" },
  interphone_code:                 { cardId: "codes",            fieldName: "interphone_code" },
  coffre_code:                     { cardId: "codes",            fieldName: "coffre_code" },
  garage_code:                     { cardId: "codes",            fieldName: "garage_code" },
  parking_info_long:               { cardId: "parking",          fieldName: "parking_info_long" },
  infos_poubelles_long:            { cardId: "parking",          fieldName: "infos_poubelles_long" },
  infos_courses_stockage:          { cardId: "parking",          fieldName: "infos_courses_stockage" },
  regles_maison:                   { cardId: "regles",           fieldName: "regles_maison" },
  livret_url:                      { cardId: "wifi",             fieldName: "livret_url" },
  avis_google_url:                 { cardId: "wifi",             fieldName: "avis_google_url" },
}

// Lit l'état d'ouverture des cartes depuis sessionStorage (défaut TOUT FERMÉ —
// décision Dimitri : titres/sous-titres/lignes explicatives servent de sommaire,
// on ouvre ce qu'on veut ; cohérent Info FBZ + Formulaires).
function readOpenCards() {
  const allClosed = () => Object.fromEntries(CARD_IDS.map((id) => [id, false]))
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return allClosed()
    const parsed = JSON.parse(raw)
    // Merge : toute carte absente du storage est fermée par défaut.
    const base = allClosed()
    for (const id of CARD_IDS) {
      if (typeof parsed?.[id] === "boolean") base[id] = parsed[id]
    }
    return base
  } catch (_) {
    return allClosed()
  }
}

// PR #145 — résa fictive partagée entre VariableInsertPanel preview et
// MessagePreviewSection MOCK_RESA. Résa générique sans particularité, sert
// uniquement à afficher des valeurs voyageur typiques dans le panel.
const PREVIEW_MOCK_RESA = {
  guest_first_name: 'Sophie',
  guest_last_name: 'Test',
  guest_email: 'sophie.test@example.fr',
  guest_phone: '+33 6 12 34 56 78',
  guest_count: 2,
  start_date: '2026-06-01',
  end_date: '2026-06-04',
  source: 'airbnb',
  total_amount: 200,
  cleaning_fee: 40,
}

export default function LivretTab({ log, parentLog = null, cfg, prop, onUpdate, onUpdateBatch, isChambre = false, onCopyToChambres, onApplyPatchAndSave }) {
  // ADR-197 LOT 3 — sur une CHAMBRE sous-loc (isChambre, parentLog porteur de
  // sœurs), chaque section de contenu expose « 📋 Copier vers les autres chambres ».
  // sisters = les chambres sœurs (parentLog.chambres sauf la chambre courante).
  // CopyToChambresButton se masque tout seul si sisters vide → invisible sur les
  // logements plats l1..l7 et sur une chambre unique.
  const sisters = (isChambre && Array.isArray(parentLog?.chambres))
    ? parentLog.chambres.filter(c => c.id !== log.id)
    : []
  // PR 8 ADR-069 : toggle [livret/formulaires]. Audience commune voyageur,
  // séparation pédagogique. MessagePreviewSection cachée si subtab='formulaires'
  // (mitigation Pessimist R-1 : aperçu live hors contexte sinon).
  const [subtab, setSubtab] = useState('livret')

  // Front 2 (source unique Wi-Fi) — wifi_networks (livret_config) chargé ICI une
  // fois pour log.id, mutualisé entre WifiNetworksEditor (carte WiFi) ET le ctx
  // de l'aperçu MessagePreviewSection (sinon {wifi_name}/{wifi_code} ressortent
  // vides côté admin, car la source n'est PAS log.* mais livret_config).
  const [wifiNetworks, setWifiNetworks] = useState(undefined) // undefined = pas encore chargé
  useEffect(() => {
    let cancelled = false
    setWifiNetworks(undefined)
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const token = session?.access_token
        const headers = token
          ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
          : { "Content-Type": "application/json" }
        const resp = await fetch(
          `/api/beds24-mapping?action=livret_admin_data&logement_id=${encodeURIComponent(log.id)}`,
          { method: "GET", headers },
        )
        const json = await resp.json()
        if (cancelled) return
        if (!resp.ok) { setWifiNetworks([]); return }
        setWifiNetworks(Array.isArray(json?.config?.wifi_networks) ? json.config.wifi_networks : [])
      } catch (e) {
        if (!cancelled) { console.error("[LivretTab] chargement wifi_networks échoué", e); setWifiNetworks([]) }
      }
    })()
    return () => { cancelled = true }
  }, [log.id])

  // PR #145 — Panneau d'insertion de variables Mustache. Mémorise l'élément
  // textarea actif + le fieldName pour pouvoir insérer au curseur sur click.
  const [insertPanel, setInsertPanel] = useState(null) // { textareaEl, fieldName, currentValue }

  // FRONT 2 — état d'ouverture des 7 cartes (session, global, défaut tout ouvert).
  const [openCards, setOpenCards] = useState(readOpenCards)
  useEffect(() => {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(openCards)) } catch (_) {}
  }, [openCards])
  const toggleCard = useCallback((cardId) => {
    setOpenCards((prev) => ({ ...prev, [cardId]: !prev[cardId] }))
  }, [])
  // Ouvre (sans refermer) un bloc — utilisé par les CTA « Compléter »/« Modifier »
  // et le bouton d'en-tête « Compléter les blocs ». Présentation pure.
  const ensureOpenCard = useCallback((cardId) => {
    setOpenCards((prev) => (prev[cardId] !== false ? prev : { ...prev, [cardId]: true }))
  }, [])

  // Registre des refs au niveau BLOC (≠ fieldRefs au niveau champ) : permet de
  // scroller vers un bloc depuis l'en-tête (« Compléter les blocs ») ou les CTA.
  const cardRefs = useRef({})
  const registerCardRef = useCallback((cardId, el) => {
    if (el) cardRefs.current[cardId] = el
    else delete cardRefs.current[cardId]
  }, [])
  const scrollToCard = useCallback((cardId) => {
    setTimeout(() => {
      const el = cardRefs.current[cardId]
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }, 60)
  }, [])
  const openAndScroll = useCallback((cardId) => {
    ensureOpenCard(cardId)
    scrollToCard(cardId)
  }, [ensureOpenCard, scrollToCard])

  // FRONT 2 — registre des refs de champs, indexé par fieldName. Permet de
  // scroller + surligner le champ source quand on clique une variable dans
  // l'aperçu. `highlightField` est le fieldName momentanément mis en évidence.
  const fieldRefs = useRef({})
  const [highlightField, setHighlightField] = useState(null)
  const registerFieldRef = useCallback((fieldName, el) => {
    if (el) fieldRefs.current[fieldName] = el
    else delete fieldRefs.current[fieldName]
  }, [])

  // FRONT 2 — clic sur une variable surlignée de l'aperçu : déplie la carte
  // source, scrolle vers le champ et le met en évidence brièvement.
  const handleVarClick = useCallback((varName) => {
    const target = VAR_TO_CARD[varName]
    if (!target) return // orpheline → non cliquable
    setOpenCards((prev) => (prev[target.cardId] ? prev : { ...prev, [target.cardId]: true }))
    setHighlightField(target.fieldName)
    // Laisse le temps au dépliage (re-render) avant de scroller.
    setTimeout(() => {
      const el = fieldRefs.current[target.fieldName]
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }, 60)
    setTimeout(() => setHighlightField(null), 1600)
  }, [])
  // ADR-197 : le résolveur ignore désormais parentLog (chambres autonomes) — la
  // preview lit directement le logement (self), plus d'attache parent.
  const logForPreview = log
  // Résa de test pour preview valeurs : on prend MOCK_RESA simple — la
  // résa réelle n'est pas critique pour l'insertion (le panel affiche les
  // valeurs logement principalement).
  const previewCtx = { reservation: PREVIEW_MOCK_RESA, logement: logForPreview, cfg, language: 'fr', wifi_networks: wifiNetworks }

  function handleRequestInsert(fieldName, textareaEl, currentValue) {
    setInsertPanel({ textareaEl, fieldName, currentValue })
  }

  function handleInsertVariable(varName) {
    const panel = insertPanel
    if (!panel) return
    const textareaEl = panel.textareaEl
    const insert = `{${varName}}`
    if (!textareaEl) {
      // Fallback : append en fin de valeur
      onUpdate(panel.fieldName, (panel.currentValue || '') + insert)
      setInsertPanel(null)
      return
    }
    const start = textareaEl.selectionStart ?? textareaEl.value.length
    const end = textareaEl.selectionEnd ?? textareaEl.value.length
    const before = textareaEl.value.slice(0, start)
    const after = textareaEl.value.slice(end)
    const newValue = before + insert + after
    onUpdate(panel.fieldName, newValue)
    setInsertPanel(null)
    // Restaurer focus + curseur après re-render React
    setTimeout(() => {
      textareaEl.focus()
      const cursorPos = start + insert.length
      try { textareaEl.setSelectionRange(cursorPos, cursorPos) } catch (_) {}
    }, 50)
  }

  // Dérivation présentation pure (gate DA : 0 écriture cfg) — complétude des
  // blocs depuis l'état déjà chargé, pour le cercle de progression + le tri
  // « À compléter » / « Renseignés ».
  const filledMap = useMemo(
    () => Object.fromEntries(CARD_IDS.map((id) => [id, isCardFilled(id, log, wifiNetworks)])),
    [log, wifiNetworks],
  )
  const todoIds = CARD_IDS.filter((id) => !filledMap[id])
  const doneIds = CARD_IDS.filter((id) => filledMap[id])
  const filledCount = doneIds.length
  const totalCount = CARD_IDS.length

  // Corps de chaque bloc (children) — extrait dans une fonction pour pouvoir
  // placer un bloc dans le groupe « À compléter » OU « Renseignés » sans
  // dupliquer le markup. Contenu/handlers/paths INCHANGÉS (gate DA).
  const renderCardBody = (cardId) => {
    switch (cardId) {
      case "caracteristiques":
        return (
          <CaracteristiquesSection
            log={log} onUpdate={onUpdate} onUpdateBatch={onUpdateBatch} sisters={sisters}
            onCopyToChambres={onCopyToChambres}
            registerFieldRef={registerFieldRef} highlightField={highlightField}
          />
        )
      case "adresse":
        return (
          <>
            <CopyToChambresButton sourceLog={log} sisters={sisters} sectionId="adresse" onCopyToChambres={onCopyToChambres} />
            <LivretField
              label="Adresse complète (voyageur)" fieldName="adresse_complete"
              value={log.adresse_complete} onUpdate={onUpdate}
              placeholder="ex: 12 rue d'Alsace, 31000 Toulouse"
              registerFieldRef={registerFieldRef} highlightField={highlightField}
            />
            <LivretField
              label="Précision (étage, accès, etc.)" fieldName="adresse_precision"
              value={log.adresse_precision} onUpdate={onUpdate}
              type="textarea" rows={2} placeholder="ex: Bâtiment A, RDC droite"
              registerFieldRef={registerFieldRef} highlightField={highlightField}
            />
            {/* Ville du logement — variable {ville} des messages Direct. La
                ville est une donnée d'ADRESSE : elle vit donc dans CETTE carte,
                là où le propriétaire cherche l'adresse (et non dans un onglet
                technique). Écrit `public_address.ville`, EXACTEMENT le path lu
                par les résolveurs ; aucun écran ne l'écrivait avant.
                Placé APRÈS l'adresse complète et AVANT le détecteur de barème :
                le détecteur continue de parser `adresse_complete` seule, il
                n'utilise pas ce champ (aucun couplage introduit). */}
            <div
              ref={(el) => registerFieldRef?.("public_address.ville", el)}
              style={fieldWrapStyle(highlightField === "public_address.ville")}
            >
              <VilleLogementEditor
                log={log} isChambre={isChambre}
                onUpdate={onUpdate} onApplyPatchAndSave={onApplyPatchAndSave}
              />
            </div>
            {/* LOT TAXE 0.4 (commit D) — détection du barème taxe de séjour à
                partir de l'adresse saisie (CP+ville, normalisation Q5).
                « Appliquer » écrit log.taxe_sejour via onApplyPatchAndSave
                (save AWAITÉ, erreur remontée — Q1 : sans barème, aucun devis
                Direct, bloc résa « bientôt disponible »). */}
            {typeof onApplyPatchAndSave === "function" && (
              <TaxeBaremeDetector log={log} cfg={cfg} onApplyPatchAndSave={onApplyPatchAndSave} />
            )}
          </>
        )
      case "codes":
        return (
          <CodesAccesSection
            log={log} onUpdate={onUpdate} sisters={sisters}
            onCopyToChambres={onCopyToChambres}
            registerFieldRef={registerFieldRef} highlightField={highlightField}
          />
        )
      case "wifi":
        return (
          <>
            {/* ADR-197 / Front 2 — pas de « copier vers les chambres » WiFi : la
                source est livret_config.wifi_networks au niveau LOGEMENT (partagée),
                pas un champ de chambre à propager. Source unique Wi-Fi : SSID +
                mot de passe éditables ici, écrivant livret_config.wifi_networks. */}
            <div
              ref={(el) => registerFieldRef?.("wifi_networks", el)}
              style={fieldWrapStyle(
                highlightField === "wifi_networks" ||
                highlightField === "wifi_instructions" ||
                highlightField === "wifi_name" ||
                highlightField === "wifi_code",
              )}
            >
              <WifiNetworksEditor
                logement_id={log.id} wifiNetworks={wifiNetworks} parentManaged
                onSaved={(nets) => setWifiNetworks(nets)}
              />
            </div>
            {/* Livret externe (optionnel) — variable {livret_url}. */}
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #eef1f5" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: DS.navy, marginBottom: 8 }}>
                <Link2 size={14} strokeWidth={1.9} /> Livret externe (optionnel)
              </div>
              <LivretField
                label="URL livret externe" fieldName="livret_url"
                value={log.livret_url} onUpdate={onUpdate}
                placeholder="https://livret.fullbnbzen.com/..."
                registerFieldRef={registerFieldRef} highlightField={highlightField}
              />
            </div>
            {/* PARTIE B (ADR-272) — Lien avis Google (optionnel). Variable
                {avis_google_url}, consommée par le message de départ
                (depart_voyageur, envoi gaté sur sa présence côté sweep Direct).
                Champ standard self-only : onUpdate('avis_google_url', v) écrit
                log.avis_google_url — EXACTEMENT le path que lit le résolveur
                effectiveLogValue(l, 'avis_google_url'). Vide par défaut. */}
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #eef1f5" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: DS.navy, marginBottom: 8 }}>
                <Star size={14} strokeWidth={1.9} /> Lien avis Google (optionnel)
              </div>
              <LivretField
                label="Lien avis Google" fieldName="avis_google_url"
                value={log.avis_google_url} onUpdate={onUpdate}
                placeholder="https://g.page/… — à remplir quand tes pages Google avis seront prêtes"
                registerFieldRef={registerFieldRef} highlightField={highlightField}
              />
              <AvisUrlHint value={log.avis_google_url} />
            </div>
          </>
        )
      case "acces":
        return (
          <>
            <LivretField
              label="Instructions accès bâtiment" fieldName="instructions_acces_batiment"
              value={log.instructions_acces_batiment} onUpdate={onUpdate}
              onRequestInsert={handleRequestInsert} type="textarea" rows={5}
              placeholder="ex: Sonnez à l'interphone FULLBNBZEN. Le code de la porte d'entrée est 4521A."
              registerFieldRef={registerFieldRef} highlightField={highlightField}
            />
            <LivretField
              label="Instructions accès appartement" fieldName="instructions_acces_appartement"
              value={log.instructions_acces_appartement} onUpdate={onUpdate}
              onRequestInsert={handleRequestInsert} type="textarea" rows={5}
              placeholder="ex: 2e étage, porte de droite. Boîte à clés à droite de la porte, code 1234."
              registerFieldRef={registerFieldRef} highlightField={highlightField}
            />
          </>
        )
      case "parking":
        return (
          <>
            <LivretField
              label="Parking (instructions complètes)" fieldName="parking_info_long"
              value={log.parking_info_long} onUpdate={onUpdate}
              onRequestInsert={handleRequestInsert} type="textarea" rows={4}
              placeholder="ex: Parking public gratuit rue d'Alsace, à 50m. Zone bleue le samedi matin."
              registerFieldRef={registerFieldRef} highlightField={highlightField}
            />
            <LivretField
              label="Poubelles (jours de collecte, instructions)" fieldName="infos_poubelles_long"
              value={log.infos_poubelles_long} onUpdate={onUpdate}
              onRequestInsert={handleRequestInsert} type="textarea" rows={4}
              placeholder="ex: Containers gris (ordures) et jaunes (tri) au coin de la rue. Sortie le mardi soir."
              registerFieldRef={registerFieldRef} highlightField={highlightField}
            />
            <LivretField
              label="Courses & stockage (commerces proches, frigo, etc.)" fieldName="infos_courses_stockage"
              value={log.infos_courses_stockage} onUpdate={onUpdate}
              onRequestInsert={handleRequestInsert} type="textarea" rows={3}
              placeholder="ex: Carrefour City à 100m. Réfrigérateur disponible, café et thé en libre-service."
              registerFieldRef={registerFieldRef} highlightField={highlightField}
            />
          </>
        )
      case "regles":
        return (
          <>
            {/* WAGON R5 LOT C — copie règles + parking + poubelles + courses +
                livret externe vers les chambres sœurs. */}
            <CopyToChambresButton sourceLog={log} sisters={sisters} sectionId="regles" onCopyToChambres={onCopyToChambres} />
            <LivretField
              label="Règles de la maison" fieldName="regles_maison"
              value={log.regles_maison} onUpdate={onUpdate}
              onRequestInsert={handleRequestInsert} type="textarea" rows={6}
              placeholder={"ex: Non-fumeur · Pas de fêtes · Animaux acceptés sur demande · Check-out impeccable apprécié"}
              registerFieldRef={registerFieldRef} highlightField={highlightField}
            />
            {/* Les 2 règles STRUCTURÉES (animaux / fumeur) — 3 états, le défaut
                « Non renseigné » laissant la clé absente de la config. Save
                awaité + erreur affichée (cf. ReglesSejourEditor). Self-only :
                jamais de cascade parent → chambre. */}
            <ReglesSejourEditor
              log={log} onUpdate={onUpdate} onApplyPatchAndSave={onApplyPatchAndSave}
            />
          </>
        )
      default:
        return null
    }
  }

  return (
    <div>
      {/* Toggle Livret / Formulaires / Modules (Phase A.4 ADR-114) — segmented
          control DA (pilule active marine plein, icônes lucide trait fin). */}
      <div style={{display:"flex",gap:6,marginBottom:14,background:DS.card,borderRadius:DS.radiusCard,padding:5,border:`0.5px solid ${DS.border}`}}>
        {[
          ['livret',         'Instructions', ClipboardList],
          ['formulaires',    'Formulaires',  FileText],
          ['livret_modules', 'Livret',       BookOpen],
        ].map(([id, label, Ico]) => {
          const on = subtab === id
          return (
            <button key={id} onClick={() => setSubtab(id)} type="button"
              style={{
                flex: 1, padding: "10px 14px", border: "none", borderRadius: 8,
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
                background: on ? DS.marine : "transparent",
                color: on ? "#fff" : DS.textSecondary,
                fontWeight: on ? 700 : 600, fontSize: 13, cursor: "pointer", fontFamily: DS.fontBody,
              }}>
              <Ico size={15} strokeWidth={1.8} /> {label}
            </button>
          )
        })}
      </div>

      {subtab === 'formulaires' && (
        // R-2 mitigation : passer explicitement log + onUpdate uniquement.
        // FormulairesTab ne supporte pas parentLog / isChambre / cfg / prop.
        <FormulairesTab log={log} onUpdate={onUpdate} />
      )}

      {subtab === 'livret_modules' && (
        // Phase A.4 (ADR-114) — éditeur drag-drop modules livret voyageur.
        // Container auto-suffisant : fetch livret_admin_data au mount.
        // PAS de modification LogementCard.jsx (Option II ADR-114 D-1).
        <LivretModulesEditor log={log} cfg={cfg} />
      )}

      {subtab === 'livret' && (
      <>
      {/* Maquette #1 (habillage DA) — en-tête de fiche + cercle de progression,
          puis deux groupes (« À compléter » en grille 2 col, « Blocs renseignés »
          en liste), enfin l'aperçu live (panneau ardoise = outil de vérification).
          PUR LAYOUT (gate DA) : chaque bloc garde son contenu/handlers/paths cfg ;
          progression + tri = dérivation présentation pure (0 écriture cfg).
          Contact conciergerie SUPPRIMÉ de l'onglet (source unique cfg.co ; ADR-199). */}

      <InstructionsHeader
        log={log}
        prop={prop}
        filledCount={filledCount}
        totalCount={totalCount}
        todoCount={todoIds.length}
        onComplete={() => { const first = todoIds[0]; if (first) openAndScroll(first) }}
      />

      {/* ─── Groupe « À COMPLÉTER » — grille 2 colonnes responsive ─── */}
      {todoIds.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <GroupHeader label="À compléter" count={todoIds.length} variant="todo" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14, alignItems: "start" }}>
            {todoIds.map((id) => (
              <BlocCard
                key={id} cardId={id} variant="todo"
                openCards={openCards} toggleCard={toggleCard}
                registerCardRef={registerCardRef} onCta={() => openAndScroll(id)}
              >
                {renderCardBody(id)}
              </BlocCard>
            ))}
          </div>
        </div>
      )}

      {/* ─── Groupe « BLOCS RENSEIGNÉS » — liste pleine largeur ─── */}
      {doneIds.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <GroupHeader label="Blocs renseignés" count={doneIds.length} variant="done" />
          <div style={{ display: "grid", gap: 12 }}>
            {doneIds.map((id) => (
              <BlocCard
                key={id} cardId={id} variant="done"
                openCards={openCards} toggleCard={toggleCard}
                registerCardRef={registerCardRef} onCta={() => openAndScroll(id)}
                preview={cardPreview(id, log, wifiNetworks)}
              >
                {renderCardBody(id)}
              </BlocCard>
            ))}
          </div>
        </div>
      )}

      {/* ═════ Aperçu live message type — panneau ardoise (outil de vérification),
          TOUJOURS OUVERT, pleine largeur, JAMAIS repliable. ═════ */}
      <MessagePreviewSection log={log} cfg={cfg} prop={prop} wifiNetworks={wifiNetworks} onVarClick={handleVarClick} />
      </>
      )}

      {/* PR #145 — panel d'insertion de variables Mustache */}
      {insertPanel && (
        <VariableInsertPanel
          log={log}
          ctx={previewCtx}
          onInsert={handleInsertVariable}
          onClose={() => setInsertPanel(null)}
        />
      )}
    </div>
  )
}

// ── Sous-titres des blocs (sommaire, visibles carte fermée) ─────────
//
// Lignes explicatives « Sert à : … » — consommateurs RÉELS vérifiés dans le code
// (audit + correction Dimitri carte Codes). Mentionnent les variables type
// {wifi_instructions} / {regles_maison} consommées par chaque bloc.
const CARD_SUBTITLES = {
  caracteristiques: "Sert à : limite de voyageurs du site direct · horaires de check-in/out des messages et du contrat · capacité affichée sur le site.",
  adresse:          "Sert à : adresse envoyée au voyageur dans les messages d'arrivée · adresse du contrat de location · livret voyageur (dérivé).",
  codes:            "Sert à : codes envoyés au voyageur dans les messages (variables {box_code}, {door_code}…) · fiche d'accès du compte ménage.",
  wifi:             "Sert à : variable {wifi_instructions} des messages (Wi-Fi structuré, source unique) · bouton lien {livret_url}.",
  acces:            "Sert à : instructions d'entrée (bâtiment puis appartement) envoyées au voyageur dans les messages d'arrivée.",
  parking:          "Sert à : infos pratiques (parking, poubelles, courses) envoyées au voyageur dans les messages d'arrivée.",
  regles:           "Sert à : règles du message de bienvenue ET de votre site direct (variable {regles_maison}).",
}

// Aperçu court d'un bloc renseigné (dérivation présentation pure, 0 écriture).
// Affiché sous le titre dans la liste « Blocs renseignés » (maquette #1).
function cardPreview(cardId, log, wifiNetworks) {
  const s = (v) => (v == null ? "" : String(v).trim())
  let txt = ""
  switch (cardId) {
    case "caracteristiques": txt = s(log.floor); break
    case "adresse":          txt = s(log.adresse_complete); break
    case "codes":            txt = s(log.box_code) ? `Code boîte à clés : ${s(log.box_code)}` : ""; break
    case "wifi": {
      const net = Array.isArray(wifiNetworks) ? wifiNetworks.find((n) => s(n?.ssid)) : null
      txt = net ? `Wi-Fi : ${s(net.ssid)}` : (s(log.livret_url) ? "Livret externe configuré" : "")
      break
    }
    case "acces":   txt = s(log.instructions_acces_batiment) || s(log.instructions_acces_appartement); break
    case "parking": txt = s(log.parking_info_long) || s(log.infos_poubelles_long) || s(log.infos_courses_stockage); break
    case "regles":  txt = s(log.regles_maison); break
    default:        txt = ""
  }
  if (!txt) return ""
  return txt.length > 90 ? `${txt.slice(0, 90)}…` : txt
}

// ── Cercle de progression (SVG pur) ─────────────────────────────────
function ProgressRing({ value, total, size = 56, stroke = 6 }) {
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const ratio = total > 0 ? Math.max(0, Math.min(1, value / total)) : 0
  const offset = c * (1 - ratio)
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={DS.cielSoft} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={DS.ciel} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset .4s ease" }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        fontFamily: DS.fontDisplay, color: DS.marineDark, lineHeight: 1,
      }}>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{value}/{total}</span>
      </div>
    </div>
  )
}

// ── En-tête de fiche Instructions (maquette #1) ─────────────────────
//
// Contexte logement (titre Fraunces + sous-ligne id · proprio · capacité) +
// cercle de progression + CTA corail « Compléter les blocs » (ouvre/scrolle le
// 1er bloc vide). Présentation pure : `onComplete` ne fait qu'ouvrir/scroller.
function InstructionsHeader({ log, prop, filledCount, totalCount, todoCount, onComplete }) {
  const propName = propDisplayName(prop)
  const cap = log.capacity || {}
  const metaParts = [
    log.id,
    propName,
    cap.max_persons != null ? `${cap.max_persons} pers.` : null,
    cap.bedrooms != null ? `${cap.bedrooms} chambre${cap.bedrooms > 1 ? "s" : ""}` : null,
  ].filter(Boolean)
  const allDone = todoCount === 0
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
      background: DS.card, borderRadius: DS.radiusCard, padding: "16px 18px",
      border: `0.5px solid ${DS.border}`, boxShadow: "0 1px 3px rgba(33,83,135,.05)",
      marginBottom: 16,
    }}>
      <ProgressRing value={filledCount} total={totalCount} />
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontFamily: DS.fontDisplay, fontSize: 19, fontWeight: 600, color: DS.marineDark, lineHeight: 1.2 }}>
          {log.nom_complet || log.nom}
        </div>
        <div style={{ fontSize: 12, color: DS.textMuted, marginTop: 3 }}>
          {metaParts.join(" · ")}
        </div>
        <div style={{ fontSize: 12.5, color: allDone ? DS.successText : DS.warningText, fontWeight: 600, marginTop: 6 }}>
          {allDone
            ? "Tous les blocs sont renseignés"
            : `${todoCount} bloc${todoCount > 1 ? "s" : ""} à compléter`}
        </div>
      </div>
      {!allDone && (
        <button type="button" onClick={onComplete} className="fbz-press"
          style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            background: DS.corail, color: "#fff", border: "none",
            borderRadius: DS.radiusControl, padding: "10px 18px",
            fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: DS.fontBody,
          }}>
          Compléter les blocs <ArrowRight size={15} strokeWidth={2} />
        </button>
      )}
    </div>
  )
}

// ── En-tête de groupe (« À compléter » ambre / « Blocs renseignés » ciel) ──
function GroupHeader({ label, count, variant }) {
  const todo = variant === "todo"
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 2px 10px" }}>
      <span style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase",
        color: todo ? DS.warningText : DS.marine,
      }}>
        {label}
      </span>
      <span style={{
        fontSize: 11, fontWeight: 700, lineHeight: 1, padding: "3px 8px", borderRadius: 999,
        background: todo ? DS.warningBg : DS.cielSoft,
        color: todo ? DS.warningText : DS.marine,
      }}>
        {count}
      </span>
      <span style={{ flex: 1, height: 1, background: DS.border }} />
    </div>
  )
}

// ── BlocCard (maquette #1) — carte de bloc repliable, habillage DA ──────
//
// Remplace l'ancienne LivretCard (SectionCard) UNIQUEMENT côté présentation :
// même handler `toggleCard(cardId)` + même état `openCards` + mêmes children
// (renderCardBody) → contenu/handlers/paths cfg/data-testid INTACTS (gate DA).
// L'en-tête est un <button aria-expanded> (le titre y vit → `.closest('button')`
// des tests retombe dessus) ; le CTA « Compléter »/« Modifier » est un bouton
// FRÈRE (jamais imbriqué). Variante `todo` = accent ambre + chip ; `done` =
// accent ciel + check + aperçu de contenu.
function BlocCard({ cardId, variant, openCards, toggleCard, registerCardRef, onCta, preview, children }) {
  const meta = CARDS[cardId]
  const open = openCards[cardId] !== false
  const Icon = meta.Icon
  const todo = variant === "todo"
  const iconBg = todo ? DS.warningBg : DS.cielSoft
  const iconCol = todo ? DS.warning : DS.marine
  const accent = todo ? DS.warning : DS.ciel
  return (
    <div
      ref={(el) => registerCardRef?.(cardId, el)}
      style={{
        background: DS.card, borderRadius: DS.radiusCard,
        border: `1px solid ${DS.border}`, borderLeft: `4px solid ${accent}`,
        overflow: "hidden", scrollMarginTop: 90,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "13px 14px" }}>
        <button
          type="button" onClick={() => toggleCard(cardId)} aria-expanded={open}
          style={{
            flex: 1, minWidth: 0, display: "flex", alignItems: "flex-start", gap: 10,
            background: "transparent", border: "none", textAlign: "left",
            cursor: "pointer", padding: 0, fontFamily: DS.fontBody,
          }}
        >
          <ChevronRight
            size={15} strokeWidth={2} aria-hidden="true"
            style={{ marginTop: 6, flexShrink: 0, color: DS.textMuted, transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}
          />
          <span style={{
            flexShrink: 0, width: 30, height: 30, borderRadius: 8,
            background: iconBg, color: iconCol,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon size={16} strokeWidth={1.9} />
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontFamily: DS.fontDisplay, fontSize: 15, fontWeight: 600, color: DS.marineDark }}>
                {meta.title}
              </span>
              {todo ? (
                <span style={{
                  fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 999,
                  background: DS.warningBg, color: DS.warningText,
                }}>
                  À compléter
                </span>
              ) : (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 999,
                  background: DS.successBg, color: DS.successText,
                }}>
                  <Check size={11} strokeWidth={2.6} /> Renseigné
                </span>
              )}
            </span>
            <span style={{ display: "block", fontSize: 11.5, color: DS.textSecondary, marginTop: 4, lineHeight: 1.45 }}>
              {todo ? CARD_SUBTITLES[cardId] : (preview || CARD_SUBTITLES[cardId])}
            </span>
          </span>
        </button>
        {todo ? (
          <button
            type="button" onClick={onCta}
            style={{
              flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 5,
              background: DS.corail, color: "#fff", border: "none",
              borderRadius: DS.radiusControl, padding: "8px 14px",
              fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: DS.fontBody,
            }}
          >
            Compléter <ArrowRight size={13} strokeWidth={2} />
          </button>
        ) : (
          <button
            type="button" onClick={onCta}
            style={{
              flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 5,
              background: "transparent", color: DS.marine,
              border: `1px solid ${DS.border}`, borderRadius: DS.radiusControl,
              padding: "7px 12px", fontSize: 12.5, fontWeight: 600,
              cursor: "pointer", fontFamily: DS.fontBody,
            }}
          >
            <PenLine size={12} strokeWidth={2} /> Modifier
          </button>
        )}
      </div>
      {open && <div style={{ padding: "2px 14px 16px" }}>{children}</div>}
    </div>
  )
}

// ── CaracteristiquesSection (WAGON R5 B2/B4) ────────────────────────
//
// Caractéristiques physiques du logement déménagées depuis InfoFbzTab
// (carte ⚙️ Logement, supprimée par ce lot). Source de vérité unique =
// la zone Instructions. Deux familles :
//   - Capacité max : path cfg `capacity.max_persons` (inchangé).
//   - Surface (m²) : path cfg `capacity.surface_m2` (micro-lot surface,
//     GO Dimitri 2026-08-01) — entier positif facultatif, servi sur la page
//     logement publique (ligne titre) + liste vitrine WordPress.
//   - Horaires check-in/check-out : paths `schedule.checkin_start` /
//     `schedule.checkout_time` (inchangés).
//
// ADR-197 (LOT 1) : l'héritage chambre sous-loc (bandeau « Horaires hérités »
// + cascade self→parent) a été RETIRÉ — les chambres ont leur propre schedule
// seedé (LOT 0). Lecture/écriture self uniquement.
//
// Le helper updateNested reproduit EXACTEMENT celui d'InfoFbzTab
// (onUpdate(parentKey, {...current, [field]: value})) — les consommateurs
// (resolveLogTime, api/sites.js, contrat-html, templates) lisent les mêmes
// champs : ZÉRO changement de consommation, pur déménagement UI.
//
// ═══════════════════════════════════════════════════════════════════════
// CORRECTIF « heure d'arrivée » (2026-08-05) — l'écran mentait au voyageur
// ═══════════════════════════════════════════════════════════════════════
// AVANT : cet éditeur — le SEUL de l'app pour les horaires — lisait et
// écrivait UNIQUEMENT `schedule.checkin_start` / `schedule.checkout_time`.
// Or les 4 consommateurs qui fabriquent ce que le voyageur reçoit
// (lib/horaires.js, lib/template-variables.js, lib/contrat-html.js,
// lib/livret-derive.js) lisent le champ PLAT `checkin_time`/`checkout_time`
// en PRIORITÉ (field-first, ADR-199 / LOT E) et ne retombent sur
// `schedule.*` que si le plat est vide. AUCUN écran n'écrivait le plat.
//
// Conséquence constatée en PRODUCTION (cfg lu le 2026-08-05) :
//   • lc1/lc2/lc3 : `checkin_time:"17h"` ET `schedule.checkin_start:"16:00"`
//     → l'écran affichait 16:00, le voyageur recevait 17h, et bouger le
//     sélecteur n'avait AUCUN effet sur les messages envoyés.
//   • l3 et l8 : `checkin_time:"17h"` sans schedule → l'écran affichait le
//     défaut 16:00 alors que le voyageur recevait bien 17h (2e mensonge,
//     que l'audit n'avait pas vu).
//   • défaut check-out affiché 11:00 alors que la cascade retient 10:00.
//
// APRÈS : lecture via resolveCheckinCheckout (LA cascade qui fait foi) →
// l'écran affiche exactement ce que le voyageur reçoit. Écriture du champ
// PLAT (autoritaire) ET de `schedule.*` maintenu EN MIROIR, pour que le
// fallback ne puisse plus jamais contredire la valeur affichée et pour ne
// rien casser chez les lecteurs directs de `schedule` (api/form-config.js →
// GuestFormView, copy-to-chambres, mapping Beds24).
//
// Choix ASSUMÉ : on aligne l'ÉDITEUR sur les consommateurs, et non l'inverse.
// Basculer les consommateurs en schedule-first aurait changé l'heure
// réellement communiquée pour lc1/lc2/lc3 (17h → 16h) — donc des voyageurs
// envoyés devant une porte fermée une heure trop tôt. Ici, ZÉRO message ne
// change : seul l'écran cesse de mentir. Les 9 logements sans `schedule`
// (l1→l9) sont intacts : la cascade lit leur champ plat comme avant.
function CaracteristiquesSection({ log, onUpdate, onUpdateBatch, sisters, onCopyToChambres, registerFieldRef, highlightField }) {
  function updateNested(parentKey, field, value) {
    const current = log[parentKey] || {}
    onUpdate(parentKey, { ...current, [field]: value })
  }

  // Écrit une heure sur les DEUX conventions en un seul patch : le champ plat
  // `checkin_time`/`checkout_time` (autoritaire pour tous les consommateurs)
  // et `schedule.checkin_start`/`schedule.checkout_time` (miroir legacy).
  // onUpdateBatch permet un patch atomique ; repli sur 2 onUpdate successifs
  // (sûr : patchEntity applique un transform fonctionnel côté LogementsPanel).
  function updateHoraire(kind, value) {
    const flatKey = kind === "checkin" ? "checkin_time" : "checkout_time"
    const scheduleKey = kind === "checkin" ? "checkin_start" : "checkout_time"
    const nextSchedule = { ...(log.schedule || {}), [scheduleKey]: value }
    if (onUpdateBatch) {
      onUpdateBatch({ [flatKey]: value, schedule: nextSchedule })
      return
    }
    onUpdate(flatKey, value)
    onUpdate("schedule", nextSchedule)
  }

  const capacity = log.capacity || {}
  // Valeurs AFFICHÉES = celles effectivement servies au voyageur (field-first,
  // formats sales « 17h » normalisés en « 17:00 » pour matcher les options).
  const { checkin: checkinDisplay, checkout: checkoutDisplay } = resolveCheckinCheckout(log)

  // FRONT 2 — refs pour navigation depuis l'aperçu (floor / checkin / checkout).
  const floorRef = useCallback((el) => registerFieldRef?.("floor", el), [registerFieldRef])
  const checkinRef = useCallback((el) => registerFieldRef?.("schedule.checkin_start", el), [registerFieldRef])
  const checkoutRef = useCallback((el) => registerFieldRef?.("schedule.checkout_time", el), [registerFieldRef])

  return (
    <>
      <CopyToChambresButton sourceLog={log} sisters={sisters} sectionId="caracteristiques" onCopyToChambres={onCopyToChambres} />
      <Row>
        <Field label="Nombre personnes MAX">
          <Select
            value={capacity.max_persons ?? 6}
            onChange={(v) =>
              updateNested("capacity", "max_persons", parseInt(v, 10))
            }
            options={PERSONS}
          />
        </Field>
        {/* MICRO-LOT SURFACE (GO Dimitri 2026-08-01) — path cfg
            `capacity.surface_m2`, self-only comme les autres champs capacity
            (ADR-197). Entier positif, vide autorisé (→ null : le payload
            public OMET la surface, règle « rien plutôt que faux ») ; saisie
            invalide REFUSÉE (helper pur src/lib/surface-m2.js). Affichée sur
            la ligne titre de la page logement publique (« 14 m² »). */}
        <Field label="Surface (m²)">
          <Inp
            value={capacity.surface_m2 ?? ""}
            onChange={(v) => {
              const parsed = parseSurfaceM2Input(v)
              if (parsed.accepted)
                updateNested("capacity", "surface_m2", parsed.value)
            }}
            placeholder="ex : 14"
          />
        </Field>
      </Row>

      {/* Horaires check-in/check-out (plage publique) — lecture/écriture self
          (ADR-197 : héritage chambre retiré). */}
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #eef1f5" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: DS.navy, marginBottom: 8 }}>
          ⏰ Horaires check-in/check-out
        </div>
        <Row>
          <div ref={checkinRef} style={fieldWrapStyle(highlightField === "schedule.checkin_start")}>
            <Field label="Heure début Check-in">
              <Select
                value={checkinDisplay}
                onChange={(v) => updateHoraire("checkin", v)}
                options={TIMES}
              />
            </Field>
          </div>
          <div ref={checkoutRef} style={fieldWrapStyle(highlightField === "schedule.checkout_time")}>
            <Field label="Heure de Check-out">
              <Select
                value={checkoutDisplay}
                onChange={(v) => updateHoraire("checkout", v)}
                options={TIMES}
              />
            </Field>
          </div>
        </Row>
      </div>

      {/* FRONT 2 — Composition logement (contrat) fusionnée ici (étage /
          chambres / SDB). Données utilisées dans contrat-html.js. Paths cfg
          inchangés : floor, capacity.bedrooms, capacity.bathrooms. */}
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #eef1f5" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: DS.navy, marginBottom: 8 }}>
          📐 Composition (contrat)
        </div>
        <Row>
          <div ref={floorRef} style={fieldWrapStyle(highlightField === "floor")}>
            <Field label="Étage">
              <Inp value={log.floor || ''} onChange={v => onUpdate('floor', v)} placeholder="ex: 2ème étage" />
            </Field>
          </div>
          <div />
        </Row>
        <Row>
          <Field label="Nombre de chambres">
            <Select
              value={log.capacity?.bedrooms ?? 2}
              onChange={v => onUpdate('capacity', { ...(log.capacity || {}), bedrooms: parseInt(v, 10) })}
              options={[...Array(15).keys()].map(n => ({ value: n, label: String(n) }))}
            />
          </Field>
          <Field label="Nombre de salles de bain">
            <Select
              value={log.capacity?.bathrooms ?? 1}
              onChange={v => onUpdate('capacity', { ...(log.capacity || {}), bathrooms: parseInt(v, 10) })}
              options={[...Array(15).keys()].map(n => ({ value: n, label: String(n) }))}
            />
          </Field>
        </Row>
      </div>
    </>
  )
}

// ── Atom LivretField — champ standard (ADR-197 LOT 1) ───────────────
//
// ADR-197 : les modes héritage (STRICT lecture seule / override + badges
// « 📍 Hérité » / « 📍 Édité au niveau Maison Zen » / bouton « ↺ Réinitialiser »)
// ont été RETIRÉS. Le SEED (LOT 0) a donné aux chambres lc1/lc2/lc3 leurs
// valeurs propres : chaque champ lit/écrit désormais directement log[fieldName].
// Les props `parentValue` / `isChambre` sont conservées (compat appelants) mais
// ne sont plus utilisées par le rendu.
//
// PR #145 — petit bouton "📋 Insérer variable" inline au-dessus d'un
// textarea. Style discret, déclenche onClick (qui ouvre le panel parent).
function InsertVarButton({ onClick }) {
  return (
    <div style={{display:'flex',justifyContent:'flex-end',marginBottom:4}}>
      <button type="button" onClick={onClick}
        style={{
          background:'#f0f4f9',border:`1px solid ${DS.border}`,borderRadius:6,
          padding:'3px 10px',fontSize:11,color:DS.navy,cursor:'pointer',
          fontFamily:DS.fontBody,fontWeight:600,
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#e3eef9'}
        onMouseLeave={e => e.currentTarget.style.background = '#f0f4f9'}>
        📋 Insérer variable
      </button>
    </div>
  )
}

// FRONT 2 — style d'un wrapper de champ en surbrillance (navigation depuis
// l'aperçu). Liseré + fond léger temporaires, sans déplacer la mise en page.
function fieldWrapStyle(highlighted) {
  return highlighted
    ? {
        borderRadius: 8,
        boxShadow: "0 0 0 2px #f59e0b",
        background: "#fffbeb",
        transition: "box-shadow .2s, background .2s",
        scrollMarginTop: 80,
      }
    : { borderRadius: 8, transition: "box-shadow .2s, background .2s", scrollMarginTop: 80 }
}

function LivretField({
  label, fieldName, value, onUpdate,
  type = 'input', placeholder, rows, onRequestInsert,
  registerFieldRef, highlightField,
}) {
  // PR #145 — ref pour permettre l'insertion au curseur via panel
  const textareaRef = useRef(null)
  const showInsertBtn = type === 'textarea' && typeof onRequestInsert === 'function'
  const handleInsertClick = () => onRequestInsert?.(fieldName, textareaRef.current, value)

  // FRONT 2 — enregistre le wrapper dans le registre de refs du parent pour
  // permettre le scroll/highlight depuis l'aperçu.
  const wrapRef = useCallback((el) => registerFieldRef?.(fieldName, el), [registerFieldRef, fieldName])
  const highlighted = highlightField === fieldName

  // ADR-197 (LOT 1) : champ standard, lecture/écriture self. Plus de mode
  // STRICT/override ni de fallback parent (chambres seedées LOT 0).
  return (
    <div ref={wrapRef} style={fieldWrapStyle(highlighted)}>
      <FieldFull label={label}>
        {showInsertBtn && <InsertVarButton onClick={handleInsertClick} />}
        {type === 'textarea'
          ? <Textarea ref={textareaRef} value={value || ''} onChange={v => onUpdate(fieldName, v)} placeholder={placeholder} rows={rows} />
          : <Inp value={value || ''} onChange={v => onUpdate(fieldName, v)} placeholder={placeholder} />
        }
      </FieldFull>
    </div>
  )
}

// PARTIE B (ADR-272) — indice de validation LÉGÈRE et NON bloquante pour le
// lien avis Google : n'apparaît que si l'admin a saisi une valeur qui ne
// ressemble pas à une URL https://. Vide = aucun message (champ optionnel).
// Purement informatif : n'empêche jamais la sauvegarde (save générique cfg).
function AvisUrlHint({ value }) {
  const v = value == null ? "" : String(value).trim()
  if (v === "" || /^https:\/\//i.test(v)) return null
  return (
    <div style={{ marginTop: 6, fontSize: 11.5, color: DS.warningText }}>
      Astuce : un lien avis Google commence normalement par « https:// ».
    </div>
  )
}

// ── Aperçu live message type (pattern MessageEditor.jsx:189) ───────
//
// Charge les templates voyageur actifs + 10 dernières résas du logement.
// Si aucune résa réelle, utilise une résa fictive pour démontrer le
// rendu. Switcher FR/EN/ES. whiteSpace:pre-wrap strict (anti-XSS).
//
// ADR-197 : le résolveur ne fait plus de fallback parent (chambres autonomes
// depuis LOT 0/1). La preview lit le logement directement (self).

// FRONT 2 — CSS généré : chaque variable connue (mappée à une carte) prend la
// COULEUR DE SA CARTE source via [data-var="X"] ; les variables ORPHELINES
// (présentes dans l'aperçu mais sans entrée VAR_TO_CARD) restent grises et non
// cliquables (pointer-events:none + tooltip « édité ailleurs »).
function buildVarColorCss() {
  let css = ""
  for (const [varName, { cardId }] of Object.entries(VAR_TO_CARD)) {
    const color = CARDS[cardId]?.hl || DS.navy
    // Surligne résolues ET vides de cette carte avec sa couleur, curseur pointer.
    css += `
      .preview-output [data-var="${varName}"] {
        background-color: ${color}1a;
        color: ${color};
        border-radius: 3px; padding: 0 2px; cursor: pointer;
        pointer-events: auto;
      }
      .preview-output .var-resolved[data-var="${varName}"] {
        border-bottom: 1px solid ${color}66;
      }`
  }
  return css
}
const VAR_COLOR_CSS = buildVarColorCss()

// LOT 4 (2026-08-28) — exporté pour la fiche V2 (colonne « Ce que reçoit le
// voyageur » de l'onglet Instructions + feuille « Vérifier dans un message »).
export function MessagePreviewSection({ log, cfg, prop, wifiNetworks, onVarClick }) {
  const [templates, setTemplates] = useState([])
  const [resas, setResas] = useState([])
  const [selectedSlug, setSelectedSlug] = useState('welcome_checkin_v2')
  const [selectedResaId, setSelectedResaId] = useState('')
  const [language, setLanguage] = useState('fr')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // PR #145 — toggle pour surligner aussi les variables résolues (mode 'all').
  // Off par défaut : seules les variables non résolues sont surlignées (utile
  // pour repérer les messages cassés avant envoi).
  const [highlightResolved, setHighlightResolved] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(null)
    Promise.all([
      supabase.from('messages')
        .select('id, slug, name, body_fr, body_en, body_es, category, active')
        .eq('active', true)
        .eq('category', 'voyageur')
        .order('slug'),
      supabase.from('reservations')
        .select('*')
        .eq('logement_id', log.id)
        .order('start_date', { ascending: false })
        .limit(10),
    ]).then(([m, r]) => {
      if (cancelled) return
      const tpls = m.data || []
      const rs = r.data || []
      setTemplates(tpls)
      setResas(rs)
      // Si welcome_checkin_v2 absent, fallback sur 1er template dispo
      if (!tpls.find(t => t.slug === 'welcome_checkin_v2') && tpls[0]) {
        setSelectedSlug(tpls[0].slug)
      }
      if (rs[0]) setSelectedResaId(rs[0].id)
      setLoading(false)
    }).catch(e => {
      if (cancelled) return
      setError(`Chargement échoué : ${e.message || e}`)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [log.id])

  // Mock résa fictive si aucune résa réelle pour ce logement
  const MOCK_RESA = {
    guest_first_name: 'Sophie',
    guest_last_name: 'Test',
    guest_email: 'sophie.test@example.fr',
    guest_phone: '+33 6 12 34 56 78',
    guest_count: 2,
    start_date: '2026-06-01',
    end_date: '2026-06-04',
    source: 'airbnb',
    total_amount: 200,
    cleaning_fee: 40,
  }

  const tpl = templates.find(t => t.slug === selectedSlug)
  const resa = resas.find(r => r.id === selectedResaId) || MOCK_RESA
  const body = tpl ? (tpl[`body_${language}`] || tpl.body_fr || '') : ''

  // ADR-197 : self only (plus d'attache parent — chambres autonomes).
  const logForRender = log

  // PR #145 — wrapHTML mode :
  //   highlightResolved=false → 'unresolved' (surligne uniquement vides)
  //   highlightResolved=true  → 'all' (surligne tout, mode debug visuel)
  const wrapMode = highlightResolved ? 'all' : 'unresolved'
  let renderedHTML = ''
  let renderError = null
  try {
    renderedHTML = renderTemplate(
      body,
      // Front 2 — wifi_networks injecté top-level (source unique) pour que
      // {wifi_name}/{wifi_code}/{wifi_instructions} résolvent dans l'aperçu admin.
      { reservation: resa, logement: logForRender, cfg, language, wifi_networks: wifiNetworks },
      { wrapHTML: wrapMode },
    )
  } catch (e) {
    renderError = `Erreur render : ${e.message || e}`
  }

  return (
    // Panneau ARDOISE = outil de vérification (DA §panneaux-outils). Habillage de
    // l'ENVELOPPE uniquement : le rendu voyageur (renderTemplate + .preview-output)
    // reste GELÉ (ADR-149) — seuls le cadre, le tag et les sélecteurs changent.
    <div style={{
      background: DS.catArdoiseBg, borderRadius: DS.radiusCard,
      border: `1px solid ${DS.toolBorder}`, padding: 18,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          background: DS.toolTagBg, color: DS.marine,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <Eye size={15} strokeWidth={1.9} />
        </span>
        <span style={{ fontFamily: DS.fontDisplay, fontSize: 15, fontWeight: 600, color: DS.marineDark }}>
          Aperçu live — message type
        </span>
        <span style={{
          fontSize: 9.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase",
          padding: "3px 8px", borderRadius: 999, background: DS.toolTagBg, color: DS.marine,
        }}>
          Outil de vérification
        </span>
      </div>
      <div style={{ fontSize: 11, color: DS.textSecondary, marginBottom: 14, lineHeight: 1.5 }}>
        Vérifie que les variables du Livret sont bien injectées dans tes templates messages voyageur. Vraies résas du logement utilisées (résa fictive si aucune).
      </div>
      {loading && <div style={{padding:12,fontSize:12,color:'#888'}}>Chargement des templates et résas…</div>}
      {error && <div style={{padding:12,fontSize:12,color:DS.red,background:'#fce8e8',borderRadius:6}}>❌ {error}</div>}
      {!loading && !error && (
        <>
          <Row>
            <Field label="Template">
              <Select
                value={selectedSlug}
                onChange={v => setSelectedSlug(v)}
                options={templates.length === 0
                  ? [{ value: '', label: 'Aucun template voyageur actif' }]
                  : templates.map(t => ({ value: t.slug, label: `${t.name} (${t.slug})` }))
                }
                disabled={templates.length === 0}
              />
            </Field>
            <Field label="Résa de test">
              <Select
                value={selectedResaId}
                onChange={v => setSelectedResaId(v)}
                options={resas.length === 0
                  ? [{ value: '', label: 'Aucune résa — résa fictive utilisée' }]
                  : resas.map(r => ({
                      value: r.id,
                      label: `${(r.guest_first_name || '?')} ${(r.guest_last_name || '')} — ${r.start_date}`,
                    }))
                }
                disabled={resas.length === 0}
              />
            </Field>
          </Row>
          <Row>
            <Field label="Langue">
              <Select
                value={language}
                onChange={v => setLanguage(v)}
                options={[
                  { value: 'fr', label: '🇫🇷 FR' },
                  { value: 'en', label: '🇬🇧 EN' },
                  { value: 'es', label: '🇪🇸 ES' },
                ]}
              />
            </Field>
            <div />
          </Row>
          {/* PR #145 — toggle surligner variables résolues */}
          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:11,color:'#666',marginBottom:8,cursor:'pointer',userSelect:'none'}}>
            <input type="checkbox" checked={highlightResolved}
              onChange={e => setHighlightResolved(e.target.checked)}
              style={{accentColor:DS.navy}}/>
            <Eye size={13} strokeWidth={1.9} /> Surligner les variables résolues (debug visuel)
          </label>
          {/* CSS scoped pour les variables wrappées.
              FRONT 2 — la base met TOUTE variable [data-var] en GRIS NEUTRE non
              cliquable (cas orphelin = éditée ailleurs), puis VAR_COLOR_CSS
              surcharge les variables connues avec la couleur de leur carte +
              curseur pointer. Les variables VIDES (.var-unresolved) restent
              soulignées en vague rouge pour les repérer, mais prennent la couleur
              de leur carte si connues. */}
          <style>{`
            .preview-output [data-var] {
              background-color: #eef0f3; color: #6b7280; border-radius: 3px;
              padding: 0 2px; cursor: default; pointer-events: none;
            }
            .preview-output .var-unresolved { text-decoration: underline wavy #ef4444; }
            ${VAR_COLOR_CSS}
          `}</style>
          <div
            className="preview-output"
            onClick={(e) => {
              // Délégation : lit data-var de l'élément cliqué (ou son span parent).
              const el = e.target.closest?.('[data-var]')
              const v = el?.dataset?.var
              if (v && VAR_TO_CARD[v]) onVarClick?.(v)
            }}
            style={{
            background: '#f8fafd',
            border: '1px solid #dde3ec',
            borderRadius: 8,
            padding: 14,
            fontSize: 13,
            whiteSpace: 'pre-wrap',
            lineHeight: 1.5,
            maxHeight: 400,
            overflowY: 'auto',
            color: '#333',
            fontFamily: DS.fontBody,
          }}>
            {renderError
              ? <span style={{color:DS.red}}>{renderError}</span>
              : (renderedHTML
                  ? <span dangerouslySetInnerHTML={{ __html: renderedHTML }} />
                  : <em style={{color:'#aaa'}}>Aucun contenu pour cette langue.</em>)
            }
          </div>
        </>
      )}
    </div>
  )
}

// ── CodesAccesSection (PR #142D) ────────────────────────────────────
//
// Affichage progressif des 5 codes structurés. box_code = champ principal
// toujours visible (8/8 logements l'utilisent). Les 4 autres sont masqués
// par défaut, révélables via "+ Ajouter un code". Si une valeur cfg
// existe déjà → affichage direct (jamais cacher un code rempli).
//
// Suppression : bouton ✕ → onUpdate(key, '') + masque le champ. Confirm
// modal si la valeur n'était pas vide (évite suppression accidentelle).
//
// État local visibleOptionalCodes (Set) = init depuis log + permet user
// d'ajouter manuellement. Re-sync sur changement de log.id pour ne pas
// retenir la visibilité d'un autre logement quand on switch.
const OPTIONAL_CODES = [
  { key: 'door_code',       label: "Code porte d'entrée", placeholder: 'ex: 1234A' },
  { key: 'interphone_code', label: 'Code interphone',     placeholder: 'ex: 9012'  },
  { key: 'coffre_code',     label: 'Code coffre',         placeholder: 'ex: 3456'  },
  { key: 'garage_code',     label: 'Code garage',         placeholder: 'ex: 7890'  },
]

function initialVisibleSet(log) {
  const set = new Set()
  for (const { key } of OPTIONAL_CODES) {
    const v = log?.[key]
    if (v != null && String(v).trim() !== '') set.add(key)
  }
  return set
}

function CodesAccesSection({ log, onUpdate, sisters, onCopyToChambres, registerFieldRef, highlightField }) {
  const [visibleOptionalCodes, setVisibleOptionalCodes] = useState(() => initialVisibleSet(log))
  // FRONT 2 — ref du box_code (champ principal) pour navigation depuis l'aperçu.
  const boxRef = useCallback((el) => registerFieldRef?.("box_code", el), [registerFieldRef])
  const [showAddMenu, setShowAddMenu] = useState(false)
  const menuRef = useRef(null)

  // Re-sync quand on change de logement (log.id différent)
  useEffect(() => {
    setVisibleOptionalCodes(initialVisibleSet(log))
    setShowAddMenu(false)
  }, [log?.id])

  // FRONT 2 — si on navigue vers un code optionnel masqué (clic variable dans
  // l'aperçu), le révéler pour que le scroll/highlight tombe sur un champ visible.
  useEffect(() => {
    if (highlightField && OPTIONAL_CODES.some(c => c.key === highlightField)) {
      setVisibleOptionalCodes(prev => {
        if (prev.has(highlightField)) return prev
        const next = new Set(prev); next.add(highlightField); return next
      })
    }
  }, [highlightField])

  // Click outside dropdown → ferme
  useEffect(() => {
    if (!showAddMenu) return
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowAddMenu(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [showAddMenu])

  function addCode(key) {
    setVisibleOptionalCodes(prev => {
      const next = new Set(prev); next.add(key); return next
    })
    setShowAddMenu(false)
  }

  function removeCode(key) {
    const meta = OPTIONAL_CODES.find(c => c.key === key)
    const currentVal = log?.[key]
    const hasValue = currentVal != null && String(currentVal).trim() !== ''
    if (hasValue) {
      if (!window.confirm(`Supprimer "${meta?.label}" (valeur actuelle : ${currentVal}) ?`)) return
    }
    setVisibleOptionalCodes(prev => {
      const next = new Set(prev); next.delete(key); return next
    })
    if (hasValue) onUpdate(key, '')
  }

  const visibleList = OPTIONAL_CODES.filter(c => visibleOptionalCodes.has(c.key))
  const hiddenList  = OPTIONAL_CODES.filter(c => !visibleOptionalCodes.has(c.key))

  return (
    <>
      <CopyToChambresButton sourceLog={log} sisters={sisters} sectionId="codes" onCopyToChambres={onCopyToChambres} />
      {/* Code boîte à clés — toujours visible (champ principal) */}
      <Row>
        <div ref={boxRef} style={fieldWrapStyle(highlightField === "box_code")}>
          <Field label="Code boîte à clés">
            <Inp value={log.box_code || ''} onChange={v => onUpdate('box_code', v)} placeholder="ex: 5678" />
          </Field>
        </div>
        <div />
      </Row>

      {/* Codes optionnels visibles : 1 par ligne avec bouton supprimer */}
      {visibleList.map(c => (
        <div key={c.key}
          ref={(el) => registerFieldRef?.(c.key, el)}
          style={{display:'grid',gridTemplateColumns:'1fr auto',gap:8,alignItems:'end',marginBottom:8,...fieldWrapStyle(highlightField === c.key)}}>
          <Field label={c.label}>
            <Inp
              value={log?.[c.key] || ''}
              onChange={v => onUpdate(c.key, v)}
              placeholder={c.placeholder}
            />
          </Field>
          <button type="button" onClick={() => removeCode(c.key)}
            title={`Supprimer ${c.label}`}
            aria-label={`Supprimer ${c.label}`}
            style={{
              background:'transparent',border:`1px solid ${DS.border}`,borderRadius:8,
              padding:'10px 12px',cursor:'pointer',color:'#888',fontSize:14,
              fontFamily:DS.fontBody,height:38,
            }}>
            ✕
          </button>
        </div>
      ))}

      {/* Bouton ajouter + dropdown */}
      {hiddenList.length > 0 && (
        <div ref={menuRef} style={{position:'relative',marginTop:6}}>
          <button type="button" onClick={() => setShowAddMenu(s => !s)}
            style={{
              width:'100%',padding:'10px 14px',
              background:'#f8fafd',border:`1px dashed ${DS.navy}55`,borderRadius:8,
              color:DS.navy,fontSize:13,fontWeight:600,cursor:'pointer',
              fontFamily:DS.fontBody,textAlign:'left',
            }}>
            + Ajouter un code ({hiddenList.length} disponible{hiddenList.length > 1 ? 's' : ''})
          </button>
          {showAddMenu && (
            <div style={{
              position:'absolute',top:'calc(100% + 4px)',left:0,right:0,
              background:'#fff',border:`1px solid ${DS.border}`,borderRadius:8,
              boxShadow:'0 4px 16px rgba(0,0,0,.08)',zIndex:10,overflow:'hidden',
            }}>
              {hiddenList.map(c => (
                <button key={c.key} type="button" onClick={() => addCode(c.key)}
                  style={{
                    display:'block',width:'100%',textAlign:'left',
                    padding:'10px 14px',background:'transparent',border:'none',
                    fontSize:13,color:DS.navy,cursor:'pointer',fontFamily:DS.fontBody,
                    borderBottom:`1px solid ${DS.border}`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0f4f9'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  + {c.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}
